/** localStorage keys and migration for missions board + catalog */

import type { MissionRow, MissionUpdate, MissionsCatalog, StatusDef, PriorityDef } from './missionTypes.ts';

export const ROWS_KEY_V2 = 'spontane_missions_board_v2';
export const ROWS_KEY_V1 = 'spontane_missions_board_v1';
export const CATALOG_KEY = 'spontane_missions_catalog_v1';

export const DEFAULT_CATALOG: MissionsCatalog = {
  people: ['Unassigned', 'Alex Cohen', 'Jordan Levi', 'Sam Klein', 'Taylor Mizrahi'],
  personTones: [0, 1, 2, 3, 4],
  statuses: [
    { id: 'st_not_started', label: 'Not started', variant: 0 },
    { id: 'st_in_progress', label: 'Working on it', variant: 3 },
    { id: 'st_done', label: 'Done', variant: 5 },
  ],
  priorities: [
    { id: '__none__', label: '—', variant: 0 },
    { id: 'pr_critical', label: 'Critical', variant: 4 },
    { id: 'pr_high', label: 'High', variant: 2 },
    { id: 'pr_medium', label: 'Medium', variant: 1 },
    { id: 'pr_low', label: 'Low', variant: 6 },
  ],
};

function newId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}_${crypto.randomUUID().slice(0, 8)}`;
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

export function newMissionId(): string {
  return newId('m');
}

export function newUpdateId(): string {
  return newId('u');
}

/** Normalize catalog JSON from localStorage or Firestore */
export function normalizeCatalogFromUnknown(parsed: unknown): MissionsCatalog {
  try {
    if (!parsed || typeof parsed !== 'object') return JSON.parse(JSON.stringify(DEFAULT_CATALOG)) as MissionsCatalog;
    const p = parsed as MissionsCatalog;
    if (!p?.people?.length) p.people = DEFAULT_CATALOG.people.slice();
    if (!p?.statuses?.length) p.statuses = JSON.parse(JSON.stringify(DEFAULT_CATALOG.statuses));
    if (!p?.priorities?.length) p.priorities = JSON.parse(JSON.stringify(DEFAULT_CATALOG.priorities));
    p.statuses = p.statuses.map((s) => ({
      id: s.id || newId('st'),
      label: s.label || 'Status',
      variant: typeof s.variant === 'number' ? Math.min(7, Math.max(0, s.variant)) : 0,
    }));
    p.priorities = p.priorities.map((pr) => ({
      id: pr.id || newId('pr'),
      label: pr.label || 'Priority',
      variant: typeof pr.variant === 'number' ? Math.min(7, Math.max(0, pr.variant)) : 0,
    }));
    const n = p.people.length;
    if (!p.personTones || !Array.isArray(p.personTones)) {
      p.personTones = p.people.map((_, i) => i % 8);
    } else {
      while (p.personTones.length < n) p.personTones.push(p.personTones.length % 8);
      p.personTones = p.personTones.slice(0, n).map((t) => (typeof t === 'number' ? Math.min(7, Math.max(0, t)) : 0));
    }
    return p;
  } catch {
    return JSON.parse(JSON.stringify(DEFAULT_CATALOG)) as MissionsCatalog;
  }
}

export function loadCatalog(): MissionsCatalog {
  try {
    const raw = localStorage.getItem(CATALOG_KEY);
    if (!raw) return normalizeCatalogFromUnknown(null);
    return normalizeCatalogFromUnknown(JSON.parse(raw));
  } catch {
    return normalizeCatalogFromUnknown(null);
  }
}

export function saveCatalog(c: MissionsCatalog) {
  localStorage.setItem(CATALOG_KEY, JSON.stringify(c));
}

/** Legacy row shape from v1 */
interface LegacyRow {
  id?: string;
  mission?: string;
  person?: string;
  status?: string;
  priority?: string;
  endDate?: string;
  details?: string;
}

function migrateLegacyRows(raw: LegacyRow[], catalog: MissionsCatalog): MissionRow[] {
  const statusMap: Record<string, string> = {
    not_started: 'st_not_started',
    in_progress: 'st_in_progress',
    done: 'st_done',
  };
  const priorityMap: Record<string, string> = {
    '': '__none__',
    critical: 'pr_critical',
    high: 'pr_high',
    medium: 'pr_medium',
    low: 'pr_low',
  };
  const fbPri =
    catalog.priorities.find((p) => p.id === '__none__')?.id || catalog.priorities[0]?.id || '__none__';

  return raw.map((r) => ({
    id: r.id || newMissionId(),
    mission: r.mission ?? '',
    person: catalog.people.includes(String(r.person)) ? String(r.person) : catalog.people[0],
    statusId: statusMap[r.status || ''] || catalog.statuses[0]?.id || 'st_not_started',
    priorityId: priorityMap[r.priority ?? ''] ?? fbPri,
    endDate: r.endDate ?? '',
    details: r.details ?? '',
    updates: [],
    archived: false,
  }));
}

export function reconcileRows(rows: MissionRow[], catalog: MissionsCatalog): MissionRow[] {
  return rows.map((r) => normalizeRow(r, catalog));
}

/** Normalize mission rows from JSON (e.g. Firestore) */
export function normalizeMissionRowsFromUnknown(arr: unknown, catalog: MissionsCatalog): MissionRow[] {
  if (!Array.isArray(arr) || arr.length === 0) return [emptyRow(catalog)];
  return arr.map((r) => normalizeRow(r as Partial<MissionRow>, catalog));
}

export function normalizeRow(r: Partial<MissionRow>, catalog: MissionsCatalog): MissionRow {
  const validStatus = catalog.statuses.some((s) => s.id === r.statusId);
  const validPri = catalog.priorities.some((p) => p.id === r.priorityId);
  const validPerson = catalog.people.some((p) => p === r.person);
  const fallbackPriority =
    catalog.priorities.find((p) => p.id === '__none__')?.id || catalog.priorities[0]?.id || '__none__';
  return {
    id: r.id || newMissionId(),
    mission: r.mission ?? '',
    person: validPerson ? String(r.person) : catalog.people[0],
    statusId: validStatus ? String(r.statusId) : catalog.statuses[0]?.id || 'st_not_started',
    priorityId: validPri ? String(r.priorityId) : fallbackPriority,
    endDate: r.endDate ?? '',
    details: r.details ?? '',
    updates: Array.isArray(r.updates) ? r.updates.map((u) => normalizeUpdate(u)) : [],
    archived: Boolean(r.archived),
  };
}

function normalizeUpdate(u: Partial<MissionUpdate>): MissionUpdate {
  return {
    id: u.id || newUpdateId(),
    body: u.body ?? '',
    createdAt: u.createdAt || new Date().toISOString(),
    authorEmail: typeof u.authorEmail === 'string' ? u.authorEmail : undefined,
    authorDisplayName: typeof u.authorDisplayName === 'string' ? u.authorDisplayName : undefined,
  };
}

export function loadRows(catalog: MissionsCatalog): MissionRow[] {
  try {
    const v2 = localStorage.getItem(ROWS_KEY_V2);
    if (v2) {
      const parsed = JSON.parse(v2) as Partial<MissionRow>[];
      return normalizeMissionRowsFromUnknown(parsed, catalog);
    }
    const v1 = localStorage.getItem(ROWS_KEY_V1);
    if (v1) {
      const parsed = JSON.parse(v1) as LegacyRow[];
      const migrated = migrateLegacyRows(parsed, catalog);
      localStorage.setItem(ROWS_KEY_V2, JSON.stringify(migrated));
      return migrated.length ? migrated : [emptyRow(catalog)];
    }
  } catch {
    /* fall through */
  }
  return [emptyRow(catalog)];
}

export function saveRows(rows: MissionRow[]) {
  localStorage.setItem(ROWS_KEY_V2, JSON.stringify(rows));
}

export function emptyRow(catalog: MissionsCatalog): MissionRow {
  return {
    id: newMissionId(),
    mission: '',
    person: catalog.people[0],
    statusId: catalog.statuses[0]?.id || 'st_not_started',
    priorityId: catalog.priorities.find((p) => p.id === '__none__')?.id || catalog.priorities[0]?.id || '__none__',
    endDate: '',
    details: '',
    updates: [],
    archived: false,
  };
}

export function clampVariant(v: number): number {
  return Math.min(7, Math.max(0, Math.floor(v)));
}

/** Tone index 0–7 for a person name using catalog.personTones */
export function toneForPerson(catalog: MissionsCatalog, personName: string): number {
  const i = catalog.people.indexOf(personName);
  if (i < 0) return Math.abs(personName.charCodeAt(0) || 48) % 8;
  const tones = catalog.personTones ?? [];
  return typeof tones[i] === 'number' ? clampVariant(tones[i]) : i % 8;
}

export function addStatus(_catalog: MissionsCatalog): StatusDef {
  return { id: newId('st'), label: 'New status', variant: 0 };
}

export function addPriority(_catalog: MissionsCatalog): PriorityDef {
  return { id: newId('pr'), label: 'New priority', variant: 2 };
}
